// Code Next — ONE answer key (C).
//
// Every "Show answer" / "Show me" / "Insert answer" in Code Next used to come
// from a per-track adapter (codeNextHelp/tracks/c.js) that re-derived C from
// the flow on its own, and drifted from codegen: a combined
// `scanf("%f %d %c", …)` where codegen reads one value per scanf, `count ++ ?;`
// for an increment, `printf("…", total:.2f)` for a decimals placeholder, the
// wrong bound on a For. Here the answer IS codegen: the lines the generated
// program holds for that step, read off the node → line map.
//
// The program is the one Run compiles (singleFileRunSource); the line map
// comes from the older walker and is carried across by remapLineMap, exactly
// as the Code view does it (ADO #580).
import { generateCodeWithLineMap } from './codeGenerator';
import { singleFileRunSource, remapLineMap } from './runSource';
import { readStatements, prototypeParts } from './codeNextCheck';

/** The node-holding child lists a flow node can carry. */
function childLists(node) {
  const out = [];
  if (!node || typeof node !== 'object') return out;
  for (const key of ['loopBody', 'afterLast', 'nodes', 'tryNodes', 'finallyNodes']) {
    if (Array.isArray(node[key])) out.push(node[key]);
  }
  for (const key of ['outcomes', 'branches', 'cases', 'catches']) {
    if (Array.isArray(node[key])) node[key].forEach((b) => { if (b && Array.isArray(b.nodes)) out.push(b.nodes); });
  }
  return out;
}

/** Find a node anywhere in a flow's node tree. */
export function findNode(nodes, id) {
  for (const n of nodes || []) {
    if (!n) continue;
    if (n.id === id) return n;
    for (const list of childLists(n)) {
      const f = findNode(list, id);
      if (f) return f;
    }
  }
  return null;
}

/** Every node id nested under `node` (not the node itself). */
export function descendantIds(node) {
  const ids = [];
  const walk = (n) => childLists(n).forEach((list) => list.forEach((c) => {
    if (!c) return;
    if (c.id) ids.push(c.id);
    walk(c);
  }));
  walk(node);
  return ids;
}

/** The id of the flow-node a Code Next step id names (`step:node:<id>` or the bare id). */
export function stepNodeId(stepId) {
  if (!stepId) return null;
  const s = String(stepId);
  return s.startsWith('step:node:') ? s.slice('step:node:'.length) : s;
}

/**
 * The generated program and its node → line map, for a C project.
 *
 * @param {{ projectName?: string, flows?: Object[], globalResources?: Object[], sessions?: Object[], files?: Object[] }} project
 * @returns {{ code: string, lines: string[], nodeLineMap: Object, singleFile: boolean } | null}
 */
export function buildCodeKey(project) {
  if (!project || !(project.flows || []).length) return null;
  const flows = project.flows;
  const globalResources = project.globalResources || [];
  const files = project.files || [];
  const singleFile = files.length <= 1;
  try {
    if (singleFile) {
      const walked = generateCodeWithLineMap({ projectName: project.projectName, flows, globalResources }, 'c');
      let code = walked.code;
      let nodeLineMap = walked.nodeLineMap;
      try {
        const runCode = singleFileRunSource({ ...project, flows, globalResources }, 'c');
        const mapped = remapLineMap(walked.code, runCode, walked.nodeLineMap);
        code = runCode;
        nodeLineMap = mapped;
      } catch { /* keep the walker's own text */ }
      return { code, lines: code.split('\n'), nodeLineMap, singleFile: true, walked };
    }
    // Several files: each file's flows on their own. A step's lines do not
    // depend on the includes around them, so this is the key for the answer;
    // Run keeps its own multi-file build.
    const perFile = {};
    const byFile = {};
    files.forEach((f) => { byFile[f.name] = flows.filter((fl) => fl.fileName === f.name); });
    Object.entries(byFile).forEach(([name, fileFlows]) => {
      if (!fileFlows.length) return;
      const isEntry = fileFlows.some((f) => f.isEntry);
      perFile[name] = generateCodeWithLineMap(
        { projectName: project.projectName, flows: fileFlows, globalResources },
        'c',
        isEntry ? {} : { skipMain: true },
      );
    });
    return { perFile, singleFile: false };
  } catch {
    return null;
  }
}

/** Remove the indent the lines share. */
function dedent(lines) {
  const indents = lines.filter((l) => l.trim()).map((l) => l.match(/^\s*/)[0].length);
  const cut = indents.length ? Math.min(...indents) : 0;
  return lines.map((l) => l.slice(cut));
}

const isCommentOnly = (l) => /^\s*(\/\/.*|\/\*.*\*\/)\s*$/.test(l);

/**
 * A node's OWN lines: its range less the ranges of the nodes nested inside it.
 *
 * Only NODE ranges are taken away. A Decision's outcome ranges are in the same
 * map and each begins on its own `if (…) {` / `} else if (…) {` / `} else {`
 * line; taking those away too (as getOwnLineNumbers does when handed every
 * key) leaves the Decision with nothing but its closing `}`.
 */
export function ownLineNumbers(node, nodeLineMap) {
  const range = nodeLineMap[node.id];
  if (!range) return null;
  const taken = new Set();
  descendantIds(node).forEach((id) => {
    const r = nodeLineMap[id];
    if (!r) return;
    for (let i = r.startLine; i <= r.endLine; i += 1) taken.add(i);
  });
  const own = [];
  for (let i = range.startLine; i <= range.endLine; i += 1) if (!taken.has(i)) own.push(i);
  return own;
}

function locate(key, project, flowId) {
  if (!key) return null;
  if (key.singleFile) return { lines: key.lines, nodeLineMap: key.nodeLineMap };
  const flow = (project.flows || []).find((f) => f.id === flowId);
  const file = flow && key.perFile[flow.fileName];
  if (!file) return null;
  return { lines: file.code.split('\n'), nodeLineMap: file.nodeLineMap };
}

/**
 * The exact C lines codegen writes for one step (dedented). For a Decision or
 * a loop these are its own lines only: the headers (`if (…) {`,
 * `} else if (…) {`, `} else {`, `for (…) {`) and the closing `}` — never the
 * steps inside.
 *
 * @param {Object} project { projectName, flows, globalResources, sessions, files }
 * @param {string} flowId the flow the step lives in
 * @param {string} stepId a node id, or a Code Next `step:node:<id>`
 * @param {Object} [key] a buildCodeKey(project) result to reuse
 * @returns {string[] | null} null when codegen writes nothing for the step
 */
export function expectedLinesForStep(project, flowId, stepId, key = null) {
  const id = stepNodeId(stepId);
  const flows = project?.flows || [];
  const flow = flows.find((f) => f.id === flowId);
  const node = (flow && findNode(flow.nodes, id)) || flows.map((f) => findNode(f.nodes, id)).find(Boolean);
  if (!node) return null;
  const k = key || buildCodeKey(project);
  const where = locate(k, project, flowId || flows.find((f) => findNode(f.nodes, id))?.id);
  if (!where) return null;
  const nums = ownLineNumbers(node, where.nodeLineMap);
  if (!nums || !nums.length) return null;
  const text = nums.map((n) => where.lines[n - 1]).filter((l) => l !== undefined && l.trim() && !isCommentOnly(l));
  if (!text.length) return null;
  return dedent(text);
}

/**
 * A flow's own first line, `float hra(float basic) {` (codegen's `fn:<flowId>`
 * entry). The flow's Start step is where the learner writes it.
 *
 * @returns {{ lines: string[], lineNums: number[] } | null}
 */
export function functionHeaderForFlow(project, flowId, key = null) {
  const k = key || buildCodeKey(project);
  const where = locate(k, project, flowId);
  const r = where && where.nodeLineMap[`fn:${flowId}`];
  if (!r) return null;
  const nums = [];
  for (let i = r.startLine; i <= r.endLine; i += 1) nums.push(i);
  const text = nums.map((n) => where.lines[n - 1]).filter((l) => l !== undefined && l.trim());
  return text.length ? { lines: dedent(text), lineNums: nums } : null;
}

/** A flow's prototype line, `float hra(float basic);` (codegen's `proto:<flowId>`). */
export function functionPrototypeForFlow(project, flowId, key = null) {
  const k = key || buildCodeKey(project);
  const where = locate(k, project, flowId);
  if (!where) return null;
  const r = where.nodeLineMap[`proto:${flowId}`];
  if (r) {
    const nums = [];
    for (let i = r.startLine; i <= r.endLine; i += 1) nums.push(i);
    const text = nums.map((n) => where.lines[n - 1]).filter((l) => l !== undefined && l.trim());
    return text.length ? { lines: dedent(text), lineNums: nums } : null;
  }
  // A Contract writes the prototypes from its cards, unmapped: find the line by its name.
  const flow = (project.flows || []).find((f) => f.id === flowId);
  if (!flow || !flow.name) return null;
  const at = where.lines.findIndex((l) => /\);\s*$/.test(l) && /^\S/.test(l) && (() => {
    const p = prototypeParts(l);
    return !p.error && p.name === flow.name;
  })());
  return at >= 0 ? { lines: [where.lines[at].trim()], lineNums: [at + 1] } : null;
}

const isDeclLine = (l) => {
  if (!l || !l.trim() || /^\s*(\/\/|#|typedef\b|struct\s+\w+\s*\{|enum\b|union\s+\w+\s*\{)/.test(l)) return false;
  try {
    const st = readStatements(l);
    return st.length > 0 && st.every((x) => x.s === 'decl' && x.decls.length);
  } catch (e) { return false; }
};

/** The declaration lines that follow line `after` (1-based), as { lines, lineNums }. */
function declBlock(lines, after) {
  const nums = [];
  for (let n = after + 1; n <= lines.length && isDeclLine(lines[n - 1]); n += 1) nums.push(n);
  return nums.length ? { lines: dedent(nums.map((n) => lines[n - 1])), lineNums: nums } : null;
}

/**
 * A flow's own declarations (its Resources card): the lines right after its
 * first line — `int main(void) {` or `float hra(float basic) {`.
 */
export function localDeclarationsForFlow(project, flowId, key = null) {
  const k = key || buildCodeKey(project);
  const where = locate(k, project, flowId);
  if (!where) return null;
  const flow = (project.flows || []).find((f) => f.id === flowId);
  let head = null;
  if (flow && flow.isEntry) {
    const at = where.lines.findIndex((l) => /^\s*int\s+main\s*\(/.test(l));
    head = at >= 0 ? at + 1 : null;
  } else {
    const r = where.nodeLineMap[`fn:${flowId}`];
    head = r ? r.endLine : null;
  }
  return head ? declBlock(where.lines, head) : null;
}

/** The global declarations: the lines under `// Global Variables`, above main. */
export function globalDeclarations(project, key = null) {
  const k = key || buildCodeKey(project);
  if (!k || !k.singleFile) return null;
  const at = k.lines.findIndex((l) => /^\s*\/\/\s*Global Variables/.test(l));
  return at >= 0 ? declBlock(k.lines, at + 1) : null;
}

/**
 * The answer as the hint panels show it: the step's lines, with `// ?` in
 * each block body so a Decision / loop answer is a shape to fill, as before.
 *
 * @param {string[] | null} lines expectedLinesForStep(...)
 * @returns {string | null}
 */
export function answerFromLines(lines) {
  if (!lines || !lines.length) return null;
  const out = [];
  lines.forEach((l, i) => {
    out.push(l);
    const next = lines[i + 1];
    if (/\{\s*$/.test(l) && next !== undefined && /^\s*\}/.test(next)) {
      const indent = l.match(/^\s*/)[0];
      out.push(`${indent}    // ?`);
    }
  });
  return out.join('\n');
}

/**
 * A function that gives a node's codegen answer, for the hint builders. Memo
 * the key once per flow change and hand this to buildGuidedSteps / the help
 * card.
 *
 * @returns {(node: Object) => string | null}
 */
export function makeAnswerFor(project, flowId, key = null) {
  const k = key || buildCodeKey(project);
  return (node) => {
    if (!node || !node.id) return null;
    return answerFromLines(expectedLinesForStep(project, flowId, node.id, k));
  };
}

/**
 * The help card with codegen's answer in place of the adapter's. Where the card
 * showed its answer as the syntax too (input / output / assignment / loop /
 * return), the syntax follows.
 */
export function withKeyAnswer(helpCard, node, answerFor) {
  if (!helpCard || !node || typeof answerFor !== 'function') return helpCard;
  const answer = answerFor(node);
  if (!answer) return helpCard;
  const old = helpCard.getContent ? helpCard.getContent() : null;
  return {
    ...helpCard,
    syntax: helpCard.syntax && helpCard.syntax === old ? answer : helpCard.syntax,
    getContent: () => answer,
  };
}
